import { cn } from '@neo/lib/utils'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@neo/primitives/accordion'
import { CircleCheck, CircleX } from 'lucide-react'
import { getMetricExpectedResponse, getMetricLabel } from '../constants'
import {
  extractMessage,
  type FieldExtractor,
  getMetricFieldConfig,
  isVariablesField,
  isYesNoValue,
  type YesNoExtractor,
} from '../metric-utils'
import type { EvalMetric } from '../types'
import { JsonDisplay } from './json-display'

interface MetricRowProps {
  metric: EvalMetric
  actualResponse?: string
  expectedResponse?: string
}

interface MetricDetailRowProps {
  label: string
  value: string | React.ReactNode
  preserveWhitespace?: boolean
}

const MetricDetailRow = ({ label, value, preserveWhitespace = true }: MetricDetailRowProps) => (
  <div className="grid grid-cols-[3fr_9fr] gap-4">
    <div className="text-p-500 text-secondary">{label}</div>
    {preserveWhitespace ? (
      <div className="text-p-400 text-secondary rounded whitespace-pre-wrap break-words">
        {value}
      </div>
    ) : (
      value
    )}
  </div>
)

const renderFieldValue = (
  value: string | Record<string, unknown> | 'Yes' | 'No' | null,
  isVariables: boolean,
) => {
  if (value === null) {
    return <span className="text-tertiary">N/A</span>
  }

  if (isYesNoValue(value)) {
    return <span className="text-p-400 text-secondary">{value}</span>
  }

  if (isVariables) {
    return <JsonDisplay data={value} />
  }

  return (
    <span className="text-p-400 text-secondary whitespace-pre-wrap break-words">
      {String(value)}
    </span>
  )
}

export const MetricRow = ({ metric, actualResponse, expectedResponse }: MetricRowProps) => {
  const isPassed = metric.score >= metric.threshold
  const metricName = metric.name.replace('[GEval]', '')
  const expectedResponseLabel = getMetricExpectedResponse(metricName)
  const metricLabel = getMetricLabel(metricName)
  const fieldConfig = getMetricFieldConfig(metricName)

  // Ensure score is normalized to 0-1 range for display
  const normalizedScore = Math.max(0, Math.min(metric.score, 1))

  // Extract actual and expected values based on metric config
  let actualValue: string | Record<string, unknown> | 'Yes' | 'No' | null = null
  let expectedValue: string | Record<string, unknown> | null = null
  let actualIsVariables = false

  if (fieldConfig) {
    // Extract actual value
    if (fieldConfig.isYesNoExtractor) {
      // YesNoExtractor (takes score and threshold)
      actualValue = (fieldConfig.extractActual as YesNoExtractor)(
        actualResponse,
        metric.score,
        metric.threshold,
      )
    } else {
      // FieldExtractor
      actualValue = (fieldConfig.extractActual as FieldExtractor)(actualResponse)
    }

    // Extract expected value if needed
    if (fieldConfig.showExpected && fieldConfig.extractExpected) {
      expectedValue = fieldConfig.extractExpected(expectedResponse)
    }

    // Check if actual value is variables
    actualIsVariables = isVariablesField(actualValue)
  } else {
    // Fallback to full JSON display for unrecognized metrics
    actualValue = actualResponse ?? null
    expectedValue = expectedResponse ?? null
  }

  const isLoopDetection = metricName === 'Loop Detection'
  const showLoopMessage = isLoopDetection && actualResponse

  return (
    <Accordion type="single" collapsible className="w-full">
      <AccordionItem value={metric.name} className="border-none">
        <AccordionTrigger
          iconPosition="left"
          className="w-full flex items-center gap-2 py-1 hover:bg-bg2 hover:no-underline rounded px-2 transition-colors justify-start"
          data-testid={`metric-${metric.name}`}
        >
          <span className="text-s-400 text-secondary text-left">{metricLabel ?? metricName}</span>
          <span>
            {isPassed ? (
              <CircleCheck className="h-4 w-4 text-status-success" aria-label="Passed" />
            ) : (
              <CircleX className="h-4 w-4 text-status-danger" aria-label="Failed" />
            )}
          </span>
        </AccordionTrigger>
        <AccordionContent className="pl-8 pr-2 space-y-3 pb-4 mt-2">
          {/* Actual Output */}
          {fieldConfig ? (
            <MetricDetailRow
              label={fieldConfig.actualLabel}
              preserveWhitespace={!actualIsVariables && !isYesNoValue(actualValue)}
              value={renderFieldValue(actualValue, actualIsVariables)}
            />
          ) : (
            <div>
              <div className="text-p-500 text-secondary mb-2">Actual Output:</div>
              <div className="ml-2">
                <JsonDisplay data={actualResponse} />
              </div>
            </div>
          )}

          {/* Loop Detection: Also show actual message */}
          {showLoopMessage && (
            <MetricDetailRow
              label="Actual Output:"
              preserveWhitespace={true}
              value={renderFieldValue(extractMessage(actualResponse), false)}
            />
          )}

          {/* Expected Output */}
          {fieldConfig?.showExpected ? (
            fieldConfig.useDescriptiveExpected && expectedResponseLabel ? (
              <MetricDetailRow
                label={fieldConfig.expectedLabel || 'Expected Output:'}
                value={
                  <div className="text-p-400 text-secondary italic">{expectedResponseLabel}</div>
                }
              />
            ) : fieldConfig.extractExpected ? (
              <MetricDetailRow
                label={fieldConfig.expectedLabel || 'Expected Output:'}
                preserveWhitespace={expectedValue !== null && !isVariablesField(expectedValue)}
                value={
                  expectedValue !== null
                    ? renderFieldValue(expectedValue, isVariablesField(expectedValue))
                    : renderFieldValue(null, false)
                }
              />
            ) : null
          ) : !fieldConfig ? (
            expectedResponseLabel ? (
              <MetricDetailRow
                label="Expected Output:"
                value={
                  <div className="text-p-400 text-secondary italic">{expectedResponseLabel}</div>
                }
              />
            ) : (
              <div>
                <div className="text-p-500 text-secondary mb-2">Expected Output:</div>
                <div className="ml-4">
                  <JsonDisplay data={expectedResponse} />
                </div>
              </div>
            )
          ) : null}

          {/* Score with Progress Bar */}
          <MetricDetailRow
            label="Score:"
            preserveWhitespace={false}
            value={
              <div className="flex items-center gap-3">
                <div className="h-2 bg-st2 rounded-full overflow-hidden flex-1">
                  <div
                    className={cn('h-full transition-all', {
                      'bg-status-danger': !isPassed,
                      'bg-status-success': isPassed,
                    })}
                    style={{ width: `${normalizedScore * 100}%` }}
                  />
                </div>
                <span className="text-xs-500 font-jetbrains tabular-nums">
                  {normalizedScore.toFixed(2)}
                </span>
              </div>
            }
          />

          {/* Reason */}
          <MetricDetailRow label="Reason:" value={metric.reason} />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}
